import { AppProvider, useApp } from "./context/AppContext.js";
import { LoginScreen } from "./components/LoginScreen/LoginScreen.js";
import { Dashboard } from "./components/Dashboard/Dashboard.js";
import { Analytics } from "./components/Analytics/Analytics.js";
import { Entries } from "./components/Entries/Entries.js";
import { Clients } from "./components/Clients/Clients.js";
import { Settings } from "./components/Settings/Settings.js";
import { InvoicePreview } from "./components/InvoicePreview/InvoicePreview.js";
import { AscenteLogo } from "./components/common/AscenteLogo.js";
import { EntryForm } from "./components/Entries/EntryForm.js";

const TABS = ["dashboard", "analytics", "entries", "clients", "settings"] as const;

function AppShell() {
  const {
    isLoggedIn, userName, logout,
    syncStatus, syncError, lastSync, refreshData,
    darkMode, setDarkMode,
    activeTab, setActiveTab,
    rangeStart, setRangeStart, rangeEnd, setRangeEnd,
    showAddEntry, setShowAddEntry, editEntry, setEditEntry,
    handleAddEntry, handleUpdateEntry,
    clients, config,
    invoiceDraft, setInvoiceDraft,
  } = useApp();

  if (!isLoggedIn) return <LoginScreen />;

  const activeClientList = clients.filter((c) => c.status === "active");

  return (
    <>
      {/* ── Invoice modal ── */}
      {invoiceDraft && (
        <InvoicePreview
          draft={invoiceDraft}
          config={config}
          currentUser={userName}
          onClose={() => setInvoiceDraft(null)}
        />
      )}

      {/* ── Entry form modal ── */}
      {showAddEntry && (
        <EntryForm
          entry={editEntry}
          clients={activeClientList}
          onSave={(draft) =>
            editEntry
              ? handleUpdateEntry({ ...editEntry, ...draft })
              : handleAddEntry(draft)
          }
          onClose={() => { setShowAddEntry(false); setEditEntry(null); }}
        />
      )}

      {/* ── Header ── */}
      <div className="header">
        <div className="header-left">
          <AscenteLogo fill={darkMode ? "#ffffff" : "#1a1a2e"} height={26} />
        </div>
        <div className="header-right">
          <div
            className="sync-status"
            title={syncStatus === "error" ? syncError : undefined}
            role={syncStatus === "error" ? "alert" : undefined}
          >
            <span className={`sync-dot ${syncStatus}`} />
            {syncStatus === "ok"
              ? `Synced${lastSync ? " " + lastSync.toLocaleTimeString() : ""}`
              : syncStatus === "loading"
              ? "Syncing…"
              : <span className="sync-message">{syncError || "Sync error"}</span>}
          </div>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setDarkMode(!darkMode)}
            title={darkMode ? "Light mode" : "Dark mode"}
          >
            {darkMode ? "☀️" : "🌙"}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => void refreshData()}>
            ↻ Refresh
          </button>
          <input type="date" className="sel" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} />
          <span style={{ fontSize: 13, color: "var(--gray-400)" }}>–</span>
          <input type="date" className="sel" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} />
          <button
            className="btn btn-primary btn-sm"
            onClick={() => { setEditEntry(null); setShowAddEntry(true); }}
          >
            + Add Entry
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={logout}
            title={`Signed in as ${userName}`}
          >
            {userName} ✕
          </button>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div style={{ padding: "20px 28px 0" }}>
        <div className="tabs">
          {TABS.map((t) => (
            <button
              key={t}
              className={`tab ${activeTab === t ? "active" : ""}`}
              onClick={() => setActiveTab(t)}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* ── Content ── */}
      <div className="content">
        {activeTab === "dashboard" && <Dashboard />}
        {activeTab === "analytics" && <Analytics />}
        {activeTab === "entries" && <Entries />}
        {activeTab === "clients" && <Clients />}
        {activeTab === "settings" && <Settings />}
      </div>
    </>
  );
}

export function App() {
  return (
    <AppProvider>
      <AppShell />
    </AppProvider>
  );
}
